// Fila PERSISTENTE de batidas do app (IndexedDB): sobrevive a fechar o app e reiniciar o celular.
// Cada batida tem client_id único → o servidor ignora reenvios (sem duplicar).
// O registro local só vira "sincronizado" depois que o servidor confirma a gravação da batida + foto.
import { appApi } from "@/lib/appApi";

const DB_NAME = "digiponto-app";
const STORE = "batidas_pendentes";
const listeners = new Set();
let sincronizando = false;

function abrir() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "client_id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(modo, fn) {
  const db = await abrir();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, modo);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => { db.close(); resolve(out?.result ?? out); };
    t.onerror = () => { db.close(); reject(t.error); };
    t.onabort = () => { db.close(); reject(t.error || new Error("abortado")); };
  });
}

const avisar = async () => {
  const lista = await listar().catch(() => []);
  listeners.forEach((fn) => fn(lista));
};

export const novoClientId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export async function salvar(rec) {
  // Defesa extra: sem localização válida da hora da batida, nada é gravado nem entra na fila
  if (rec.latitude == null || rec.longitude == null || !(rec.accuracy_m > 0) || !rec.gps_obtido_em) {
    throw new Error("sem localização");
  }
  await tx("readwrite", (s) => s.put(rec));
  // Confere a gravação antes de dizer ao funcionário que está salvo
  const ok = await obter(rec.client_id);
  if (!ok || ok.foto_base64 !== rec.foto_base64) throw new Error("falha ao gravar no celular");
  avisar();
}

export const obter = (id) => tx("readonly", (s) => s.get(id));
export const listar = async () => ((await tx("readonly", (s) => s.getAll())) || [])
  .sort((a, b) => (a.local_time < b.local_time ? 1 : -1));

async function atualizar(id, patch) {
  const atual = await obter(id);
  if (!atual) return;
  await tx("readwrite", (s) => s.put({ ...atual, ...patch }));
  avisar();
}

export async function descartar(id) {
  await tx("readwrite", (s) => s.delete(id));
  avisar();
}

// Tentativas antigas sem localização (versões anteriores do app) nunca serão aceitas: saem da fila e da tela
async function purgarSemLocalizacao() {
  const todos = await listar();
  const invalidos = todos.filter((r) => r.status !== "sincronizado" && (r.latitude == null || r.longitude == null));
  for (const r of invalidos) await tx("readwrite", (s) => s.delete(r.client_id));
  if (invalidos.length && navigator.onLine) {
    appApi.post("/app/gps-bloqueio", { motivo: `${invalidos.length} tentativa(s) sem localização removida(s) da fila do celular`, offline: true }).catch(() => {});
  }
}

export function assinar(fn) {
  listeners.add(fn);
  purgarSemLocalizacao().catch(() => {}).finally(() => listar().then(fn).catch(() => {}));
  return () => listeners.delete(fn);
}

async function enviarUm(rec) {
  // Localização obrigatória e só a do momento da batida — nunca completada depois
  if (rec.latitude == null || rec.longitude == null) {
    const erro = "Registro sem localização — não pode ser enviado. Descarte e registre de novo com a localização ativada.";
    await atualizar(rec.client_id, { status: "falha", erro });
    return { ok: false, falha: true, erro };
  }
  await atualizar(rec.client_id, { status: "sincronizando" });
  try {
    const r = await appApi.post("/app/batida-foto", {
      client_id: rec.client_id, foto_base64: rec.foto_base64, local_time: rec.local_time,
      instante_utc: rec.instante_utc || new Date(rec.criado_ms || Date.now()).toISOString(),
      latitude: rec.latitude, longitude: rec.longitude, accuracy_m: rec.accuracy_m,
      gps_obtido_em: rec.gps_obtido_em || null, offline: !!rec.offline, device_id: rec.device_id,
    }, { timeout: 30000 });
    if (!r.data?.ok || !r.data?.foto_confirmada) throw Object.assign(new Error("sem confirmação"), { semConfirmacao: true });
    // Confirmado: libera a foto do celular e mantém só o resumo
    await atualizar(rec.client_id, { status: "sincronizado", foto_base64: null, servidor_id: r.data.id,
      sincronizado_em: new Date().toISOString(), erro: "" });
    window.dispatchEvent(new Event("digiponto:batida-confirmada"));
    return { ok: true, data: r.data };
  } catch (e) {
    const st = e.response?.status;
    const detalhe = e.response?.data?.detail;
    if (!e.response || e.semConfirmacao || st >= 500 || st === 429 || st === 401) {
      const erro = st === 401 ? "Sessão expirada — entre no app novamente para enviar"
        : !e.response ? "Sem internet — será enviado automaticamente" : "Servidor não confirmou — nova tentativa automática";
      await atualizar(rec.client_id, { status: "pendente", erro, tentativas: (rec.tentativas || 0) + 1 });
      return { ok: false, pendente: true, erro };
    }
    await atualizar(rec.client_id, { status: "falha", erro: typeof detalhe === "string" ? detalhe : "Servidor recusou a batida" });
    return { ok: false, falha: true, erro: detalhe };
  }
}

export async function enviarAgora(client_id) {
  const rec = await obter(client_id);
  return rec ? enviarUm(rec) : { ok: false };
}

export async function sincronizarTodas(appUserId) {
  if (sincronizando) return;
  sincronizando = true;
  try {
    // Só envia as batidas de QUEM está conectado (celular compartilhado não mistura funcionários)
    const lista = (await listar()).filter((r) => !r.app_user_id || r.app_user_id === appUserId);
    for (const rec of lista.reverse()) {
      if (rec.status === "pendente" || rec.status === "sincronizando") {
        const r = await enviarUm(rec);
        if (r.pendente && !navigator.onLine) break;
      }
    }
    // Limpa resumos já confirmados há mais de 3 dias
    const limite = Date.now() - 3 * 86400000;
    for (const rec of lista) {
      if (rec.status === "sincronizado" && Date.parse(rec.sincronizado_em || 0) < limite) await descartar(rec.client_id);
    }
  } finally { sincronizando = false; }
}

export async function tentarNovamente(client_id) {
  await atualizar(client_id, { status: "pendente", erro: "" });
  return enviarAgora(client_id);
}
